export const RPE_MIN = 5;
export const RPE_MAX = 10;
export const RPE_STEP = 0.5;
export const RIR_MAX = RPE_MAX - RPE_MIN;

// i18n keys (common namespace), not text: callers translate.
export const RPE_ERROR_FORMAT = "validation.rpeFormat";
export const RIR_ERROR_FORMAT = "validation.rirFormat";
export const RPE_ERROR_ORDER = "validation.rpeOrder";

type RpeSet = { rpe?: number | string | null; rpeLower?: number | null };

export type ParsedRpe =
  | { rpe: number | "AMRAP" | null; rpeLower: number | null; error?: undefined }
  | { error: string; rpe?: undefined; rpeLower?: undefined };

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && !Number.isNaN(value);

const toRiR = (rpe: number) => +(RPE_MAX - rpe).toFixed(1);

/** Text shown for a set's RPE: "7", "7-8", "AMRAP", or RiR "2-3" (ends convert, order flips). */
export const rpeValue = (
  rpeInput?: number | string | null,
  rpeLowerInput?: number | string | null,
  isRiR = false
): string => {
  if (rpeInput === "AMRAP") return "AMRAP";
  if (rpeInput === undefined || rpeInput === null || rpeInput === "") return "";
  // forms hand back numeric strings
  const rpe = Number(rpeInput);
  if (Number.isNaN(rpe)) return String(rpeInput);
  const rpeLower =
    rpeLowerInput === undefined ||
    rpeLowerInput === null ||
    rpeLowerInput === ""
      ? undefined
      : Number(rpeLowerInput);
  if (isNumber(rpeLower) && rpeLower < rpe)
    return isRiR ? `${toRiR(rpe)}-${toRiR(rpeLower)}` : `${rpeLower}-${rpe}`;
  return String(isRiR ? toRiR(rpe) : rpe);
};

/** Planned-report value: midpoint of a range, otherwise the single value. */
export const rpeMidpoint = (set?: RpeSet | null): number | undefined => {
  if (!set || set.rpe === undefined || set.rpe === null) return undefined;
  const rpe = Number(set.rpe);
  if (set.rpe === "" || Number.isNaN(rpe)) return undefined;
  return isNumber(set.rpeLower) && set.rpeLower < rpe
    ? (set.rpeLower + rpe) / 2
    : rpe;
};

/** Value logged by tap complete: the bottom of a range. */
export const rpeBottom = (set?: RpeSet | null): number | string | undefined => {
  if (!set || set.rpe == null || set.rpe === "") return undefined;
  return isNumber(set.rpeLower) && set.rpeLower < Number(set.rpe)
    ? set.rpeLower
    : set.rpe;
};

const validEnd = (value: number, isRiR: boolean) => {
  const [min, max] = isRiR ? [0, RIR_MAX] : [RPE_MIN, RPE_MAX];
  return value >= min && value <= max && value % RPE_STEP === 0;
};

/**
 * Parses what a coach typed. RiR input is converted to RPE for storage.
 * Empty → no RPE; "amrap" (any case) alone → AMRAP; "7" → single; "7-8" → range.
 */
export const parseRpeInput = (text: unknown, isRiR = false): ParsedRpe => {
  const raw = String(text ?? "").trim();
  if (!raw) return { rpe: null, rpeLower: null };
  if (/^amrap$/i.test(raw)) return { rpe: "AMRAP", rpeLower: null };

  const format = isRiR ? RIR_ERROR_FORMAT : RPE_ERROR_FORMAT;
  const match = /^(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?$/.exec(raw);
  if (!match) return { error: format };

  const first = Number(match[1]);
  const second = match[2] === undefined ? undefined : Number(match[2]);
  if (
    !validEnd(first, isRiR) ||
    (second !== undefined && !validEnd(second, isRiR))
  )
    return { error: format };
  if (second === undefined)
    return { rpe: isRiR ? toRiR(first) : first, rpeLower: null };

  // RiR order flips: RiR 2-3 is RPE 7-8
  const [lower, upper] = isRiR
    ? [toRiR(second), toRiR(first)]
    : [first, second];
  if (isRiR ? second <= first : first >= second)
    return { error: RPE_ERROR_ORDER };
  return { rpe: upper, rpeLower: lower };
};

const INVALID_RPE_PREFIX = "invalid|";

/**
 * Form text for an unreadable entry: keeps the i18n error key and what was typed,
 * so a blocked save can show the same error and the field can keep its text.
 */
export const invalidRpeText = (error: string, typed: string) =>
  `${INVALID_RPE_PREFIX}${error}|${typed}`;

/** Splits invalidRpeText back; undefined when the text is not an unreadable entry. */
export const readInvalidRpeText = (text: unknown) => {
  if (typeof text !== "string" || !text.startsWith(INVALID_RPE_PREFIX))
    return undefined;
  const [error, ...typed] = text.slice(INVALID_RPE_PREFIX.length).split("|");
  return { error, typed: typed.join("|") };
};

/** Edit surfaces keep a range as one text cell ("7-8"), like reps: join before editing. */
export const joinRpeForEdit = (set: {
  rpe?: number | string | null;
  rpeLower?: number | string | null;
}) => {
  if (
    set.rpe !== "AMRAP" &&
    set.rpe !== "" &&
    set.rpe != null &&
    set.rpeLower
  ) {
    set.rpe = `${set.rpeLower}-${set.rpe}`;
    set.rpeLower = null;
  }
};

/**
 * Split an edited RPE cell back into rpe + rpeLower (null when it's a single
 * value, so a removed range clears). Returns an i18n error key when unreadable.
 * Numbers are an already-split set and are left alone.
 */
export const splitRpeText = (set: {
  rpe?: number | string | null;
  rpeLower?: number | null;
}): string | undefined => {
  if (typeof set.rpe !== "string") return undefined;
  const invalid = readInvalidRpeText(set.rpe);
  if (invalid) return invalid.error;
  const parsed = parseRpeInput(set.rpe);
  if (parsed.error !== undefined) return parsed.error;
  set.rpe = parsed.rpe ?? "";
  set.rpeLower = parsed.rpeLower;
  return undefined;
};

/** Form text of a stored set: "7-8" for a range, otherwise the single value. */
export const rpeFormText = (set?: {
  rpe?: number | string | null;
  rpeLower?: number | string | null;
}): string => {
  if (!set || set.rpe === undefined || set.rpe === null) return "";
  return typeof set.rpe === "number" && set.rpeLower
    ? `${set.rpeLower}-${set.rpe}`
    : String(set.rpe);
};
